import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import api from '../utils/api.js'
import type { QueryParams, Template, Notification, MaintenanceSchedule } from '../types/domain'

type Id = number | string

// ── Dashboard ────────────────────────────────────────────
export function useDashboard(params: QueryParams = {}) {
  return useQuery({
    queryKey: ['dashboard', params],
    queryFn: () => api.get<unknown>('/dashboard', { params }).then(r => r.data),
    refetchInterval: 60 * 1000,
  })
}

export function useChecklist() {
  return useQuery({
    queryKey: ['checklist'],
    queryFn: () => api.get<unknown>('/dashboard/checklist').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })
}

// ── Templates ────────────────────────────────────────────
export function useTemplates() {
  return useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<Template[]>('/templates').then(r => r.data),
  })
}
export function useCreateTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Template>) => api.post<Template>('/templates', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  })
}
export function useUpdateTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<Template> & { id: Id }) => api.put<Template>(`/templates/${id}`, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  })
}
export function useDeleteTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: Id) => api.delete(`/templates/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  })
}

// ── Notifications ─────────────────────────────────────────
export function useNotifications(params: QueryParams = {}) {
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: () => api.get<Notification[]>('/notifications', { params }).then(r => r.data),
    // Keep the current page on screen while a bigger limit / filter change
    // refetches (UX-audit N14: load-more must append, not flash a spinner).
    placeholderData: keepPreviousData,
  })
}
export function useUnreadCount() {
  return useQuery({
    queryKey: ['unread-count'],
    queryFn: () => api.get<{ count?: number } & Record<string, unknown>>('/notifications/unread-count').then(r => r.data),
    // Real-time updates via WebSocket invalidation (socket-context.jsx); polling disabled
    refetchInterval: false,
    staleTime: 25_000,
  })
}

// ── Notification Preferences ─────────────────────────────
export function useNotificationPreferences() {
  return useQuery({ queryKey: ['notification-prefs'], queryFn: () => api.get<Record<string, unknown>>('/users/me/notification-preferences').then(r => r.data) })
}
export function useUpdateNotificationPreferences() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (data: Record<string, unknown>) => api.put<Record<string, unknown>>('/users/me/notification-preferences', data).then(r => r.data), onSuccess: () => qc.invalidateQueries({ queryKey: ['notification-prefs'] }) })
}
export function usePatchNotificationPreferences() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => api.patch<Record<string, unknown>>('/users/me/notification-preferences', data).then(r => r.data),
    onSuccess: (next) => {
      qc.setQueryData(['notification-prefs'], next)
    },
  })
}

// ── Maintenance Schedules ─────────────────────────────────
export function useMaintenanceSchedules(params: QueryParams = {}) {
  return useQuery({ queryKey: ['maintenance-schedules', params], queryFn: () => api.get<MaintenanceSchedule[]>('/maintenance-schedules', { params }).then(r => r.data) })
}
export function useMaintenanceDueSoon(params: QueryParams = {}) {
  return useQuery({ queryKey: ['maintenance-due', params], queryFn: () => api.get<MaintenanceSchedule[]>('/maintenance-schedules/due-soon', { params }).then(r => r.data) })
}
export function useCreateMaintenanceSchedule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<MaintenanceSchedule>) => api.post<MaintenanceSchedule>('/maintenance-schedules', data).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['maintenance-schedules'] }); qc.invalidateQueries({ queryKey: ['maintenance-due'] }) },
  })
}
export function usePerformMaintenanceSchedule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: Id) => api.post<MaintenanceSchedule>(`/maintenance-schedules/${id}/perform`).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['maintenance-schedules'] }); qc.invalidateQueries({ queryKey: ['maintenance-due'] }) },
  })
}
export function useDeleteMaintenanceSchedule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: Id) => api.delete(`/maintenance-schedules/${id}`).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['maintenance-schedules'] }); qc.invalidateQueries({ queryKey: ['maintenance-due'] }) },
  })
}

// ── Marketplace ───────────────────────────────────────────
export function useMarketplace(params: QueryParams = {}) {
  return useQuery({ queryKey: ['marketplace', params], queryFn: () => api.get<unknown>('/marketplace', { params }).then(r => r.data) })
}

// ── Custom Domains ────────────────────────────────────────
export function useCustomDomains() {
  return useQuery({ queryKey: ['domains'], queryFn: () => api.get<unknown>('/domains').then(r => r.data) })
}
